// 遊戲常數 — 對應 Unity Script/GameLogic.cs、Data/*.asset 的已驗證數值。
// 標示 [ASSUMED] 的常數是來源未儲存/未確認、依合理慣例暫定的值，取得真實數值後需替換並移除標記。
// 詳見 ../../docs/UNITY_SOURCE_ANALYSIS.md、../../docs/PIXI_MIGRATION_PLAN.md「風險與待確認事項」。

/** 主輪數量（reel index 0-5）。已驗證：RandomReelStripData.asset 主遊戲盤面共 7 條輪帶，其中 6 條為主輪。 */
export const MAIN_REEL_COUNT = 6

/** Tracker 頂輪的 reel index（第 7 條輪帶）。已驗證：反序疊加在主輪 1-4 上。 */
export const TRACKER_REEL_INDEX = 6

/**
 * Tracker 頂輪疊加覆蓋的主輪 index（低→高，代表「被覆蓋的主輪範圍」）。
 * 已驗證：Unity GameLogic.GetPossibleWays 以 trackerStartAt=1 起算、連續 N 欄各 +1（N=頂輪顆數），
 * 組合方式數（見 logic/possibleWays.ts）用此「正序範圍」語意，與符號對應的反序無關。
 */
export const TRACKER_OVERLAY_REELS: readonly number[] = [1, 2, 3, 4]

/**
 * Tracker 頂輪「反序」疊加對應（已驗證自 Unity GameLogic.cs 三處第一手：FindWinningPositions:92、
 * GetAllWinningCells:175、GetReelLayoutLog；公式 `trackerIndex = (count-1) - (reelIndex-1)`，除錯列印
 * 標題直書 "Top Tracker (Reel 7 - Reversed)"）：trackerColumn 的 index 0 疊在「最右」被覆蓋主輪（reel 4）、
 * index 3 疊在「最左」（reel 1）。後端 reels[6] 亦按此反序排列，故畫面顯示位置與中獎判定都必須用反序，
 * 否則會與後端錯位（tracker 疊到錯的主輪、畫面左右顛倒、本地重算的中獎與後端不一致 → 看得到卻不消除）。
 * 注意：組合方式數（TRACKER_OVERLAY_REELS 的正序範圍）是 Unity GetPossibleWays 的另一套寫法、與此反序無關。
 */

/** trackerColumn 第 position 格疊加在哪個主輪 reel index（反序）；超出範圍回 undefined。 */
export function trackerReelForPosition(position: number): number | undefined {
  return TRACKER_OVERLAY_REELS[TRACKER_OVERLAY_REELS.length - 1 - position]
}

/** 主輪 reel index 對應到 trackerColumn 的哪一格（反序）；非被覆蓋主輪回 -1。 */
export function trackerPositionForReel(reelIndex: number): number {
  const rangeIndex = TRACKER_OVERLAY_REELS.indexOf(reelIndex)
  return rangeIndex === -1 ? -1 : TRACKER_OVERLAY_REELS.length - 1 - rangeIndex
}

/** 可視列數（每輪顯示幾格）。來源 RandomReelStripData.asset 未儲存此值，依使用者確認為 5 列（直行 5 格）。 */
export const VISIBLE_ROWS = 5

/** [ASSUMED] Ways-to-win 最少連續輪數。來源未確認，暫比照常見 ways-pays 慣例（由最左輪起算）。 */
export const MIN_WAYS_REELS = 3

/** Wild 符號 ID。已驗證：SymbolData.asset 的 0=WW。 */
export const WILD_SYMBOL_ID = 0

/** Scatter 符號 ID。已驗證：SymbolData.asset 的 12=SS，唯一有分級圖檔的符號。 */
export const SCATTER_SYMBOL_ID = 12

/** Scatter 觸發 Free Game 的最少數量。已驗證：SlotStateMachine 的 GetFgCount 邏輯。 */
export const SCATTER_FG_TRIGGER_COUNT = 3

/** Free Game 贈送次數。已驗證：來源固定贈送 11 次。 */
export const FG_SPIN_COUNT = 11

/**
 * 自動遊戲可選轉數。已驗證：Unity AutoSpinSetting.TotalSpinOptions = {10, 25, 50, 100, int.MaxValue}，
 * 其中 int.MaxValue 代表「無限」，本專案以 Number.POSITIVE_INFINITY 表示（顯示為 ∞）。
 */
export const AUTO_SPIN_OPTIONS: readonly number[] = [10, 25, 50, 100, Number.POSITIVE_INFINITY]

/**
 * 購買免費遊戲（Buy Bonus，BuyType=2）的花費倍率。已驗證：PayTableData.asset 的 BetRatios
 * BuyType 2 → Ratio 100，即花費＝一般下注 ×100，購買後直接進入免費遊戲。
 */
export const BUY_BONUS_MULTIPLIER = 100
/** 購買免費遊戲對應的 BuyType 值（Cmd.proto：0=一般 1=ExtraBet 2=FreeSpins 3=SuperFreeSpins）。 */
export const BUY_TYPE_FREE_SPINS = 2

/** [ASSUMED] 消除補位時生成 Wild 的機率。來源未提供轉換機率數值，暫定值。 */
export const WILD_FILL_CHANCE = 0.08

/**
 * [ASSUMED] Free Game 中每次連鎖贏分後，倍率遞增量。
 * 已驗證「CurrentMultiplier 在 FG 期間逐次連鎖遞增並保留」的行為，但確切遞增規則來源未提供，暫用「每次中獎 +1」。
 */
export const FG_MULTIPLIER_INCREMENT = 1

/**
 * Free Game 起始倍率（進入 FG 時倍率牌初始顯示值）。
 * 已驗證：Unity SlotStateMachine.ToFg_Enter 進 FG 時 `CurrentMultiplier++`（由 0→1），故起始為 1。
 */
export const FG_START_MULTIPLIER = 1

/**
 * 主倍率飛入強調的觸發門檻：FG 中某步套用倍率 > 此值時，播放大倍率字飛入倍率牌的強調動畫。
 * 已驗證：Unity DestroySymbol_Enter 的 `haveMultiplier = IsFreeGame && currentMultiplier > 1`。
 */
export const FG_MAIN_MULTIPLIER_MIN = 1

/**
 * 盤面震動的倍率門檻：FG 主倍率飛入時，倍率 ≥ 此值會額外震動盤面。
 * 已驗證：Unity ShowMainMultiplier 的 `if (currentMultiplier >= 10) ShakeBoard()`。
 */
export const FG_SHAKE_MULTIPLIER_MIN = 10

// ── Scatter 累積計量系統（Free Game 專屬，對應 Unity SlotStateMachine.CheckWin 的 FG Scatter 收集迴圈）──

/** 計量器收集槽數量／滿格點數。已驗證：Unity GamePlayUI 有 6 個 _collectPointSpine、CheckWin 的 maxPoint=6。 */
export const SCATTER_COLLECTOR_SLOTS = 6
export const SCATTER_MAX_POINT = 6

/** 計量等級上限（4 級）。已驗證：Unity GameLogic.GetLevel 回 1~4。 */
export const SCATTER_LEVEL_MAX = 4

/**
 * 依累積 Scatter 點數換算計量等級（1~4）。
 * 已驗證：Unity GameLogic.GetLevel — `<2`→1、`2-3`→2、`4-5`→3、`≥6`→4。
 */
export function scatterLevelFor(count: number): number {
  if (count < 2) return 1
  if (count < 4) return 2
  if (count < 6) return 3
  return SCATTER_LEVEL_MAX
}

/**
 * 免費遊戲中 Scatter 顯示的「野狼」分級圖上限（美術只有 S1/S2/S3 三張）。
 * 已驗證：Unity SymbolView.SetNormal 依 CellData.Level 於 FG 改用 Lv1/2/3Sprite（S1/S2/S3 野狼臉），
 * 主遊戲維持一般 SS 造型（警示牌）。計量等級（scatterLevelFor）可達 4，但野狼圖只有 3 級，故第 4 級沿用 S3。
 */
export const SCATTER_WOLF_MAX_LEVEL = 3

/**
 * [MOCK] Scatter 獎勵表：計量升級（或滿格後 retrigger）時發放的加成，對應後端 ScatterBonus{AddMultiplier,AddSpinCount}。
 * Unity 以子彈美術暗示各級加成（Lv2→X3、Lv3→X5／3spins、Lv4→8spins），但確切數值由後端 RGS 決定、來源 asset 無資料，
 * 故此處為暫定值。index 對應「第幾次發放」：升 Lv2／Lv3／Lv4 各一次；滿 6 點後每再收一顆走 retrigger（見下）。
 */
export const MOCK_SCATTER_BONUS: readonly { readonly addMultiplier: number; readonly addSpinCount: number }[] = [
  { addMultiplier: 3, addSpinCount: 0 }, // 升 Lv2（count=2）：加倍率（X3）
  { addMultiplier: 5, addSpinCount: 3 }, // 升 Lv3（count=4）：加倍率（X5）＋免費次數
  { addMultiplier: 0, addSpinCount: 8 }, // 升 Lv4／滿格（count=6）：加免費次數（8spins）
]

/** [MOCK] 滿 6 點後 retrigger：每再收一顆 Scatter 追加的免費次數（來源無數值，暫定）。 */
export const MOCK_SCATTER_RETRIGGER_SPINS = 2

/** [MOCK] 免費遊戲中「本轉沒有任何 Scatter 落地」時，主動注入 1 顆的機率（純為離線可見計量玩法）。 */
export const MOCK_SCATTER_LAND_CHANCE = 0.18

/**
 * [MOCK] 免費遊戲收集 Scatter 的注入上限：累積達此點數後不再主動注入，避免 retrigger 無限延長 FG。
 * 讓 Mock 免費遊戲至多達 Lv4＋數次 retrigger（約 28 轉內），非來源數值。
 */
export const MOCK_SCATTER_MAX_COLLECT = 9

/** 免費遊戲總轉數安全上限（含 retrigger 追加），防止極端情況無限增生。非來源數值。 */
export const FG_MAX_SPINS = 60

/**
 * Anticipation 預兆觸發的已落地 Scatter 數。已驗證：Unity SlotStateMachine 於「2 個 Scatter」
 * （_omenSymbolId=12）落地後，對後續輪做延遲/預兆特效。達此數後的剩餘輪逐輪慢速揭示＋光暈。
 */
export const ANTICIPATION_SCATTER_COUNT = 2

// ── 轉輪／演出節奏（已驗證：Unity Scene/Prefab 覆寫後的生效值；程式預設值若被覆寫以覆寫值為準） ──

/** 起輪逐組間隔（秒）。Scene 覆寫 _startInterval=0.1（程式預設 0.2）。 */
export const REEL_START_INTERVAL_S = 0.1
/** 停輪（補入結果盤面）逐組間隔（秒）。Scene 覆寫 _stopInterval=0.1。 */
export const REEL_STOP_INTERVAL_S = 0.1
/** 保底轉動時間（秒）：舊盤清空後、結果補入前的空窗，Turbo 也不縮短。ReelControllerMediator._spinDuration。 */
export const REEL_SPIN_DURATION_S = 0.4
/** 舊符號離場（轉動開始的整欄墜落清除）單顆時長（秒）。ReelStrip._dropExitDuration。 */
export const REEL_EXIT_DURATION_S = 0.3
/** 補位（結果符號自上方掉入）單顆時長（秒）。ReelStrip._fillDuration；曲線為三段（見 ReelsView.dropCellTween）。 */
export const FILL_DURATION_S = 0.4
/** 補位逐顆間隔（秒）。LandscapeUI.prefab 覆寫 _fillSymbolsInterval=0.05（程式預設 0.01）。 */
export const FILL_INTERVAL_S = 0.05
/**
 * [刻意偏離 Unity] Turbo 模式下「停輪落定」錯開等待（逐欄 REEL_STOP_INTERVAL_S、逐顆 FILL_INTERVAL_S）
 * 的縮放係數。
 *
 * Unity 的 Turbo 並沒有專屬節奏參數，而是直接借用快停那條路徑：SlotStateMachine.cs:227 在進 StopSpin 前
 * 就先呼叫 ForceStopRotation()，於是 ReelController.cs:107 的 `WhenAny(Delay(_stopInterval), _forceStopSignal)`
 * 立即返回、ReelStrip.cs:226 的 `if (IsForceStop) continue;` 跳掉逐顆間隔 ⇒ 7 條輪同時落定、無左→右順序。
 * 本移植依使用者要求保留順序性，Turbo 取一般值的一半（逐欄 0.05s、逐顆 0.025s）。
 *
 * 注意此係數「只」作用於落定；離場與快停不套用：
 *   - 離場：[使用者要求] 轉動揭示改為整欄同時掉、不再逐顆錯開（原 REEL_EXIT_INTERVAL_S 已移除）。
 *   - 離場逐欄（REEL_START_INTERVAL_S）Unity 的 Turbo 確實略過（StartRotation(!isTurbo) → ReelController.cs:84）。
 *   - 快停（forceStop）一律歸零，那是快停的本意。
 */
export const TURBO_STAGGER_SCALE = 0.5
/** 補位落定過衝高度（Unity 設計座標 px，對應 cellH=134）。ReelStrip._fillBounceHeight。 */
export const FILL_BOUNCE_DESIGN_PX = 20
/** 消除後存活符號下墜補洞時長（秒），Ease=OutBounce、全部同時。ReelStrip._dropDuration。 */
export const DROP_DURATION_S = 0.4
/** 中獎符號 win loop 展示時長（毫秒）：會接著消除時 1.5s。SymbolView.PlayWinAnimation。 */
export const WIN_LOOP_MS = 1500
/** 中獎符號 win loop 展示時長（毫秒）：不消除（Scatter 進 FG／FG 收集展示）時 2.0s。 */
export const WIN_LOOP_HOLD_MS = 2000
/**
 * 符號「落定 land」與「待機 loop」骨骼演出的時長（毫秒）。
 * Unity 兩處都硬編 1.0f：落定＝ReelStrip.DoFillEffect 尾端的 `_playEffectHandle(symbolView, "land", 1.0f, false)`、
 * 待機＝SymbolView.PlayLoopAnimation 的 `PlayAnimation(_loopAnimation, 1.0f, true)`。
 * 實測 H1/WW/SS 的每一支 land 與 loop 骨骼長度皆恰為 1.0000s，故 Unity 這個固定值與骨骼長度完全一致
 * （也因此 Unity 每 1.0s 以 SetAnimation 重掛 loop 的副作用在視覺上是無縫的）。
 * 本移植優先讀骨骼實際長度，查不到才退回此值。
 */
export const SYMBOL_STATE_ANIM_MS = 1000
/**
 * 符號狀態骨骼（land→loop）之間的過場交叉淡化秒數，消除瞬切跳動。
 * land 播完前 SYMBOL_STATE_MIX_S 秒起 Spine 會自動 crossfade 到 loop（見 SymbolView.playLand 的 addAnimation）；
 * 取一小段即可（land/loop 皆 1.0s），太長會拖泥、太短仍偏硬切。
 */
export const SYMBOL_STATE_MIX_S = 0.12
/** 非中獎符號變暗/還原的漸變時長（秒）。SymbolView.SetMask 的 DOColor。 */
export const MASK_FADE_S = 0.1
/** 非中獎符號變暗的顏色（0.502 灰）。Symbol.prefab _maskColor。 */
export const SYMBOL_MASK_TINT = 0x808080
/** Wild 變身批次結束後的延遲（毫秒）。SlotStateMachine._changeSymbolDelay=0.4（Scene 未覆寫）。 */
export const CHANGE_SYMBOL_DELAY_MS = 400
/** Wild 變身兩段動畫（原符號骨骼 win → change）各自的固定等待（毫秒）。
 *  Unity ReelController.PlayChangeAnimation 對兩段 PlayAnimation 皆寫死 1.0s。 */
export const WILD_CHANGE_STEP_MS = 1000
/** 補牌預兆（盤面 Scatter≥2 時補牌前的拉張力等待，毫秒）；Turbo 版較短。SlotStateMachine._fillOmenDelay。 */
export const FILL_OMEN_DELAY_MS = 1500
export const FILL_OMEN_DELAY_TURBO_MS = 1000
/** 開轉「會中免費遊戲」預告特效的出現機率。SlotStateMachine._showPreWinRatio。 */
export const PRE_WIN_RATIO = 0.6
/** 聽牌 Omen：已落地 Scatter 達 2 顆起聽、達 3 顆後不再聽。ReelController._targetOmenSymbolCount 與 <3 守衛。 */
export const OMEN_MAX_SCATTER = 3
/** 自動遊戲續轉間隔（毫秒），不受 Turbo 影響。SlotStateMachine._autoSpinInterval=0.4。 */
export const AUTO_SPIN_INTERVAL_MS = 400
/** 免費遊戲每轉間隔（毫秒）。SlotStateMachine._freeGameSpinInterval=0.2。 */
export const FG_SPIN_INTERVAL_MS = 200
/** 免費遊戲進場字卡關閉後的延遲（毫秒）。SlotStateMachine._freeSpinCutsceneDelay=0.4。 */
export const FG_CUTSCENE_DELAY_MS = 400
/** FG Scatter 收集全部處理完後的收尾延遲（毫秒）。Scene 覆寫 _updateFreeSpinCountDelay=0.8。 */
export const FG_COLLECT_SETTLE_MS = 800
/** 浮動計分板（WinTemp）單步贏分跑分時長（秒），Linear。WinTemp._effectDuration。 */
export const WIN_TEMP_COUNT_S = 0.3
/** 底部列贏分累計跑分時長（秒），Linear。BottomBarView.winValueTweenDuration。 */
export const BOTTOM_WIN_TWEEN_S = 0.25
/**
 * 大獎慶祝每一級的停留／跑分時長（秒），Linear。數值同 Unity WinCelebrationUI.LevelPlayTime=4，
 * 語意上＝ani_win 的 `_in`（2.000s）播完再讓 `_loop`（2.000s）完整跑一輪。
 * 執行期以骨架實際動畫長度為準（RewardOverlayView.stageHoldSeconds），此值僅作查不到動畫時的退回值。
 * ⚠️ 與 Unity 的差異：Unity 末級會依剩餘比例縮短（常不足 1s 甚至瞬跳），本移植依需求改為每級一律播滿。
 */
export const CELEBRATION_LEVEL_TIME_S = 4
/** 報獎/結算字卡自動關閉時間（毫秒）。RewardPresentationPanelMediator._autoCloseTime=5。 */
export const REWARD_AUTO_CLOSE_MS = 5000

/** 大獎分級標籤。none 為未達大獎門檻的一般結算；max 為達最高贏分上限（提前結算）。 */
export type WinTier = 'none' | 'big' | 'mega' | 'super' | 'epic' | 'max'

/**
 * 最高贏分上限倍率（總贏分 ÷ 本局總下注）。達此倍數觸發 Max-win 提前結算。
 * [CONFIRMED] Unity Localization 兩處獨立佐證為 30,000×：`game.Info_MaxWin_1`（「maximum win amount is
 * limited to 30,000x bet in both base game and free spins」）與 `game.Loading_3`（「WIN UP TO 30,000X BET」）。
 * 先前 10,000× 為誤推（僅憑截圖宣傳條猜測），已依權威來源字串修正。
 */
export const MAX_WIN_RATIO = 30000

/** 本局最高贏分（顯示單位）＝總下注 × MAX_WIN_RATIO。totalBet<=0 時回 Infinity（視為無上限）。 */
export function maxWinFor(totalBet: number): number {
  return totalBet > 0 ? totalBet * MAX_WIN_RATIO : Number.POSITIVE_INFINITY
}

/** 是否達最高贏分上限。 */
export function isMaxWin(totalWin: number, totalBet: number): boolean {
  return totalWin >= maxWinFor(totalBet)
}

/**
 * 大獎分級門檻（總贏分 ÷ 本局總下注 的倍數），由高到低排列。
 * 已驗證：Unity CustomWinCelebrationUI 的 WinAnimationData（BigWin 10x／MegaWin 25x／SuperWin 50x／EpicWin 100x）；
 * FG 結算 `isBigWin = totalWin / TotalBet >= 10` 亦用此 10x 基準。Max（30,000x，見 MAX_WIN_RATIO）為提前結算上限。
 */
export const WIN_TIER_RATIOS: readonly { readonly tier: WinTier; readonly ratio: number }[] = [
  { tier: 'max', ratio: MAX_WIN_RATIO },
  { tier: 'epic', ratio: 100 },
  { tier: 'super', ratio: 50 },
  { tier: 'mega', ratio: 25 },
  { tier: 'big', ratio: 10 },
]

/** 依「總贏分 ÷ 總下注」倍數換算大獎分級；未達 10x 回 'none'。totalBet<=0 時視為無分級。 */
export function winTierFor(totalWin: number, totalBet: number): WinTier {
  if (totalBet <= 0 || totalWin <= 0) return 'none'
  const ratio = totalWin / totalBet
  return WIN_TIER_RATIOS.find((entry) => ratio >= entry.ratio)?.tier ?? 'none'
}

/**
 * 消除連鎖的安全上限（防止極端隨機序列導致無窮/過長迴圈）。
 * 一般預期遠低於此值就會停止，純為防禦性保護，非來源數值。
 */
export const MAX_TUMBLE_STEPS = 40

/**
 * 長條形（跨格大符號）的最大高度。已驗證自 Unity（CellData.Size / CellSymbol.Size 範圍 1~4，
 * 且只有 WW/H1/H2/H3/H4/SS 有到 4x 的美術）。
 */
export const MAX_SYMBOL_SIZE = 4

/**
 * [MOCK] 每次落盤時生成長條符號的機率。已驗證：Unity 的本地輪帶一律 Size=1，長條符號只從後端
 * Spin 結果（CellSymbol.Size）而來；本專案為離線 Mock，故由此機率在落盤時主動注入長條符號以呈現該玩法。
 * 取得真實後端後改由後端結果決定，此常數移除。
 */
export const MOCK_TALL_SYMBOL_CHANCE = 0.55
